/**
 * MIRRORED FILE — twin: src/utils/packConversion.js
 * Change one, change both. `mirroredUtilParity.test.js` fails the build otherwise.
 *
 * Restating a quotation line — its price AND its quantity — in a different unit.
 *
 * ── Why this exists ───────────────────────────────────────────────────────────
 * The quote generator used to convert by reading `displayEquivalents`, a block
 * written once at generation (quotationGeneration.service.js is its only author)
 * whose ratios are stated PER THE UNIT THE LINE HAD AT THAT MOMENT. Converting a
 * line changes its `uom` and `unitPrice` underneath those ratios and nothing
 * re-anchors them, so on the second conversion they describe a unit the line no
 * longer has. QUO-00011 shipped all four consequences at once:
 *
 *   - `kgPerUom: 1` meant "one PCS weighs 1 kg". Read against the same line after
 *     it became a CASE it claims one CASE weighs 1 kg, so US$100/CASE reported as
 *     US$100/kg — the user's "once it got the wrong price which is 100 per kg".
 *   - `piecesPerOuter: 10` meant "10 PCS to the CASE", and against a CASE line it
 *     offered a CASE containing ten CASEs at US$1,000.
 *   - Nothing emitted a per-piece figure for this shape, so PCS -> CASE -> PCS
 *     had no way back and the dropdown refused a unit the line started in.
 *   - Each hop derived from the previous hop's already-`ceilPrice`d output, and
 *     ceilPrice only ever rounds UP, so a round trip ratcheted a cent per hop:
 *     10 -> 10.01 -> 10.02 -> 10.04, which is what the user watched happen.
 *
 * ── The model ─────────────────────────────────────────────────────────────────
 * A pack structure describes the PRODUCT, not any one line's current unit, so it
 * cannot go stale when the unit changes:
 *
 *   { piecesPerOuter, outerUom, kgPerPiece, litrePerPiece, metresPerPiece }
 *
 * Everything reduces to ONE PIECE as the base. `piecesPerUnit` answers "how many
 * pieces is one X", and a price in any unit is that count times the price of a
 * piece. Because every unit is defined against the same base rather than against
 * the previous answer, conversion is symmetric and exact by construction:
 * kg -> lb -> kg returns the number it started from, with no accumulated round-up.
 *
 * The caller supplies the ANCHOR — the price and unit the user last actually
 * established (generation, or a hand edit) — and every conversion is computed
 * from it rather than from its own last output. That is what makes the sequence
 * PCS -> CASE -> KG -> PCS land back on the original cent.
 *
 * ── A line is a price AND an amount ──────────────────────────────────────────
 * Both halves move together or neither is right. `convertUnitPrice` scales by
 * `to/from` and `convertQuantity` by `from/to`, so their product — the money on
 * the line — is invariant across a conversion by construction. Converting only
 * the price left an RFQ for 20 PCS reading "20 CASE" and quoted ten times over;
 * see `convertQuantity` for that report in full.
 *
 * Rounding is deliberately NOT done here. This returns full precision and the
 * caller quantises once, at the edge, when it stores or displays a price. Ceiling
 * mid-chain is exactly the ratchet described above.
 */

import {
  canonicalUnit,
  isSingleItemUnit,
  isGroupingUnit,
  isDimensionUnit,
  splitSizeText,
  measureFromText,
  isSameUnit,
} from './unitVocabulary';

/** The product's pack, independent of whatever unit a line is currently quoted in. */
export type PackStructure = {
  /** How many pieces are in one outer pack. */
  piecesPerOuter: number | null;
  /** What that outer pack is called (CASE, CARTON, BOX...). */
  outerUom: string | null;
  /** What one piece weighs, in kilograms. */
  kgPerPiece: number | null;
  /** What one piece holds, in litres. */
  litrePerPiece: number | null;
  /** What one piece measures, in metres. */
  metresPerPiece: number | null;
  /** The piece size in ounces, only when it was stated in ounces (D170.4). */
  ozPerPiece?: number;
  /** Litres in one ounce of this item, only where it states them (`ounceLitres`, D170.4). */
  litrePerOunce?: number;
};

/**
 * USC's pound: **1 kg = 2.21 lb, exactly**, so one pound is `1 / 2.21` kg.
 *
 * This is NOT the physical constant. The international avoirdupois pound is
 * 0.45359237 kg (exact by definition since 1959), which makes 1 kg = 2.2046226
 * lb. USC works to a stated business figure instead, and that figure has now
 * been chosen twice:
 *
 *   2026-09-04  the call says the system will use 2.2
 *   2026-09-07  the owner confirms 2.2 with the counter-argument in front of
 *               them (the mismatch Deborah was seeing is 2-decimal display
 *               rounding; the on-screen per-kg/per-lb ratios already read
 *               2.189-2.199)
 *   2026-09-24  **2.21**, asked for by the client on USC-BUG-0008 and confirmed
 *               by the owner after being told again that 2.2046 is the exact
 *               figure and that 2.21 is therefore slightly further from it than
 *               2.2 was. Chosen anyway, with that known. It is a stated business
 *               figure, not a defect, and not an approximation anyone should
 *               "improve".
 *
 * **What it costs:** ~0.24% against the true pound (2.21 vs 2.2046226), high on
 * every mass conversion — a per-lb price reads 0.24% LOW against physics and a
 * kg->lb quantity 0.24% high. Note the sign flipped when the figure moved from
 * 2.2 to 2.21: under 2.2 a per-lb price read 0.21% high. Deliberate and
 * accepted. Do NOT "correct" this back to 0.45359237, and do not restore 2.2.
 *
 * **Stored figures do not follow.** Every `ttPerLb` / `usdPerLb` written before
 * 2026-09-24 was computed on the 2.2 pound and is not restated — this is the
 * "fix the code, leave what old code stored" rule (D45.12). The catalogue screen
 * derives per-lb FROM per-kg through this constant, so what a person reads moves
 * immediately; a stored tier moves when its row is next costed.
 *
 * **Why it is written as a reciprocal.** The contract is stated in the kg->lb
 * direction (2.21), but the tables below are keyed "kg per unit", so the constant
 * has to be the lb->kg half. `1 / 2.21` is not exactly representable in a double,
 * but `1 / (1 / 2.21) === 2.21` IS exactly true in IEEE 754, so writing the
 * reciprocal of the stated figure gives BOTH directions exactly: 1 kg converts
 * to exactly 2.21 lb and back to exactly 1 kg, with no drift between them.
 * Spelling a rounded 0.452489 here instead would break the return trip.
 * Pinned by `mirroredUtilParity.test.js` (both twins at once) and
 * `client/src/utils/packConversion.test.ts`.
 *
 * ONE OWNER. A local copy of the pound in any other file is still forbidden —
 * that duplication is what put a six-digit 0.453592 on the quotation screen and
 * an eight-digit one in `itemGroup.MASS_IN_G`, both of which now read this.
 */
export const LB_PER_KG = 1 / 2.21;
export const OZ_PER_LB = 16;

/**
 * Kilograms in one unit, by CANONICAL name (`unitVocabulary.canonicalUnit`, which
 * folds KGS/KILO -> KG and LB/POUND -> LBS).
 */
export const KG_PER_UNIT: Record<string, number> = {
  KG: 1,
  G: 0.001,
  MG: 0.000001,
  LBS: LB_PER_KG,
  OZ: LB_PER_KG / OZ_PER_LB,
};

/**
 * Litres in one gallon: FOUR, flat. Owner's decision, 2026-09-14 (docs/plans/
 * DECISIONS.md D36): "use hard 4 lit equal to 1 gallon by default".
 *
 * Not the US gallon (3.785 L) and not the imperial one (4.546 L). USC works to
 * four the way it works to 2.21 lb a kilo (`LB_PER_KG` above) — a stated business
 * figure, not a physical constant. It replaced, the same day, a US gallon that
 * had been used for per-litre pricing only.
 *
 * **What it costs:** on a supplier's "1 GAL" jug that really holds 3.785 L, the
 * system counts ~5.7% more litres than are in it, so a per-litre price reads ~5%
 * low and a GAL -> L quantity ~5.7% high. That is the choice made. Do NOT
 * "correct" it to either physical gallon.
 *
 * It sits IN `LITRE_PER_UNIT`, so there is one gallon for every reader:
 * `itemGroup.VOLUME_IN_ML` (sizes, the per-litre tier, catalogue matching),
 * `piecesPerUnit` (the quotation Unit dropdown), the item conversion graph
 * (`unitConversionGraph`) and the generator's `getUnitConversionFactors`.
 */
export const LITRES_PER_GALLON = 4;

/**
 * Litres in one unit, same canonical names.
 *
 * PT is a recognised unit that is deliberately ABSENT. A US pint is 473 ml and
 * an imperial one 568 ml, and guessing is a 20% pricing error. Left
 * unconvertible until a human states the convention — docs/TECH_DEBT.md #144,
 * whose gallon half the owner settled on 2026-09-14 (`LITRES_PER_GALLON`).
 */
export const LITRE_PER_UNIT: Record<string, number> = {
  L: 1,
  ML: 0.001,
  GAL: LITRES_PER_GALLON,
};

/**
 * Litres one ounce fills by water (1 g = 1 ml): the ONE ounce, `KG_PER_UNIT.OZ`, read as a volume.
 * Only a stated ounce crosses, and only where no stated volume answers; KG, G, MG and LBS never
 * reach a volume (`weighsButNotOunce`). Not in LITRE_PER_UNIT: that would open KG -> OZ -> L. D170.4.
 */
export const OZ_IN_LITRES = KG_PER_UNIT.OZ;

/**
 * Litres in one ounce of THIS item: its stated `litrePerOunce` (`statedLitrePerOunce`), else the
 * water's. The one place the water figure is chosen (D170.4).
 */
export function ounceLitres(pack: { litrePerOunce?: unknown } | null | undefined): number {
  return positive(pack && pack.litrePerOunce) ?? OZ_IN_LITRES;
}

/** A weight that is not the ounce (KG, G, MG, LBS): never on either side of the water bridge. */
export function weighsButNotOunce(unit: unknown): boolean {
  const u = canonicalUnit(unit);
  return !!u && u !== 'OZ' && KG_PER_UNIT[u] != null;
}

/** One unit the ounce and the other a volume (L, ML, GAL): the pair the water bridge converts. */
export function ounceMeetsVolume(a: unknown, b: unknown): boolean {
  const ua = canonicalUnit(a);
  const ub = canonicalUnit(b);
  return (ua === 'OZ' && LITRE_PER_UNIT[ub || ''] != null) || (ub === 'OZ' && LITRE_PER_UNIT[ua || ''] != null);
}

/**
 * Metres in one unit, by canonical name. Only MTR for now — it is its own
 * base unit, the same shape KG/L are for their tables. See
 * `pricingBasis.service.js`'s `LENGTH_UNITS` comment for why this is a small,
 * self-contained addition rather than folded into a shared dimension system.
 */
export const METRE_PER_UNIT: Record<string, number> = {
  MTR: 1,
};

/**
 * A validity guard, NOT a number parser — the fields it checks are already
 * numeric (the server computes them; the client reads them back out of JSON).
 * Writing a parser here would be another `num()` clone, which invariant 8 exists
 * to prevent.
 */
function positive(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Coerce whatever was stored into the canonical pack shape, with null for every
 * fact the row does not actually state.
 *
 * Missing is not zero, and it is not one either: a row that says nothing about
 * its outer pack must make CASE UNREACHABLE rather than default to some count,
 * because a wrong count quotes a case at the price of a piece.
 */
export function normalizePackStructure(raw: unknown): PackStructure {
  const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const outer = canonicalUnit(p.outerUom);
  const piecesPerOuter = positive(p.piecesPerOuter);
  const ozPerPiece = positive(p.ozPerPiece);
  const litrePerOunce = positive(p.litrePerOunce);
  return {
    // An outer pack needs BOTH halves to be usable: a name with no count cannot
    // be priced, and a count with no name cannot be matched to a dropdown entry.
    piecesPerOuter: outer && piecesPerOuter ? piecesPerOuter : null,
    outerUom: outer && piecesPerOuter ? outer : null,
    kgPerPiece: positive(p.kgPerPiece),
    litrePerPiece: positive(p.litrePerPiece),
    metresPerPiece: positive(p.metresPerPiece),
    // A piece size stated in ounces, and the litres in one ounce where the item states them
    // (`ounceLitres`, D170.4). Each only when stated.
    ...(ozPerPiece != null ? { ozPerPiece } : {}),
    ...(litrePerOunce != null ? { litrePerOunce } : {}),
  };
}

/** A fact about one piece or the outer pack: what `piecesPerUnit` counts with. */
function statesAPiece(p: PackStructure): boolean {
  return (
    p.piecesPerOuter != null ||
    p.kgPerPiece != null ||
    p.litrePerPiece != null ||
    p.metresPerPiece != null ||
    p.ozPerPiece != null
  );
}

/** Is `unit` a volume this pack answers only through its ounces (no stated litres)? D170.4. */
function volumeByWater(unit: unknown, p: PackStructure): boolean {
  return LITRE_PER_UNIT[canonicalUnit(unit) || ''] != null && p.litrePerPiece == null && p.ozPerPiece != null;
}

/**
 * Pieces in one `fromUom` and in one `toUom`, or null when either is underivable or the pair would
 * reach a volume by water from a weight that is not the ounce (KG <-> L stays closed, D170.4).
 */
function piecesOfBoth(fromUom: unknown, toUom: unknown, p: PackStructure): { from: number; to: number } | null {
  const from = piecesPerUnit(fromUom, p);
  const to = piecesPerUnit(toUom, p);
  if (from == null || to == null || from <= 0 || to <= 0) return null;
  if ((volumeByWater(fromUom, p) && weighsButNotOunce(toUom)) || (volumeByWater(toUom, p) && weighsButNotOunce(fromUom))) return null;
  return { from, to };
}

/**
 * DOES THIS PACK STATE ANY FACT AT ALL ABOUT HOW THE PRODUCT IS PACKED?
 *
 * An EMPTY pack structure is not a pack structure. "We know nothing about this
 * row's packing" and "this row is packed one-to-one" are different facts, and
 * conflating them is invariant 8 (missing is not zero) in another costume.
 *
 * The distinction needs its own name because `packStructure` is TRUTHY either
 * way. `pricingBasis.buildDisplayEquivalentsForQuotation` writes
 * `packStructureForLine(...)` onto EVERY line unconditionally, before its own
 * price gate, so a row that states neither a piece size nor a case count still
 * ships `{piecesPerOuter: null, outerUom: null, kgPerPiece: null,
 * litrePerPiece: null, metresPerPiece: null}` — an object every `if (pack)` in
 * the browser reads as "this line has a pack on file".
 *
 * THE RULE ALREADY EXISTED AND ONLY ONE HALF OF IT WAS ENFORCED.
 * `quotationPackStructure.fillMissingPackStructures` refuses to WRITE the
 * hollow shape for exactly this reason
 * (BUSINESS_RULES#quotation-line-restatable-units — "an all-null pack is truthy
 * and would read to every caller as 'this line has a pack on file' while
 * answering none of their questions"). Nothing enforced it on the READ side, so
 * every line already carrying a hollow pack straight from generation went on
 * being treated as packed. TECH_DEBT #309.
 *
 * `outerUom` is deliberately not consulted: `normalizePackStructure` nulls it
 * unless `piecesPerOuter` came with it, so a name on its own is already gone.
 */
export function packStatesSomething(pack: unknown): boolean {
  const p = normalizePackStructure(pack);
  // An item's litres per ounce is a fact too, though it counts no pieces (D170.4).
  return statesAPiece(p) || p.litrePerOunce != null;
}

/**
 * How many PIECES one unit of `unit` contains, given the pack. Null when the
 * pack does not state enough to know — which is the signal to refuse the
 * conversion rather than to guess at it.
 */
export function piecesPerUnit(unit: unknown, pack: unknown): number | null {
  const u = canonicalUnit(unit);
  if (!u) return null;
  const p = normalizePackStructure(pack);

  // NOTHING STATED, NOTHING KNOWN. TECH_DEBT #309.
  //
  // This is the ONLY arm below that an empty pack changes: the outer arm needs
  // `outerUom`, and the kg/litre/metre arms each need their own per-piece
  // figure, so all four already answered null on an empty one. The single-item
  // arm did not — it read the unit's NAME, never the pack — so a hollow
  // structure answered `1` for TIN, BTL, JAR, DRUM and PCS, which this runtime
  // then took as evidence a pack was known. The per-kg column blanked (the
  // description reading below it was shadowed) and `perInnerUsd` was published
  // equal to the unit price under a "PCS" label: a conversion that converts
  // nothing, presented as one.
  //
  // Stated as a property of the STRUCTURE rather than of the unit, on purpose.
  // A TIN on a row that HAS a pack still holds one tin — `piecesPerUnit('TIN',
  // {piecesPerOuter: 10, outerUom: 'CASE', kgPerPiece: 1})` is still 1, and
  // PCS -> PCS through a real pack is still exactly 1. What a hollow structure
  // must not do is make that answer read as "and therefore this line is
  // convertible". Litres per ounce alone count no piece (D170.4).
  if (!statesAPiece(p)) return null;

  // The row's own outer pack, by its own name. Checked first because a name can
  // be both an outer and a single item — TIN is in SINGLE_ITEM_UNITS, but a row
  // that says "24 to a TIN" has overridden that for itself.
  if (p.outerUom && p.outerUom === u) return p.piecesPerOuter;

  if (KG_PER_UNIT[u] != null) {
    return p.kgPerPiece ? KG_PER_UNIT[u] / p.kgPerPiece : null;
  }
  if (LITRE_PER_UNIT[u] != null) {
    if (p.litrePerPiece) return LITRE_PER_UNIT[u] / p.litrePerPiece;
    // A stated volume wins; else the ounces fill the item's litres per ounce (D170.4).
    return p.ozPerPiece ? LITRE_PER_UNIT[u] / (p.ozPerPiece * ounceLitres(p)) : null;
  }
  if (METRE_PER_UNIT[u] != null) {
    return p.metresPerPiece ? METRE_PER_UNIT[u] / p.metresPerPiece : null;
  }

  // A unit that names exactly one item holds one piece.
  if (isSingleItemUnit(u)) return 1;

  // A grouping unit this row said nothing about (a BOX on a row that only
  // states CASEs). The WORD fixes no count, so there is nothing to return.
  return null;
}

/**
 * Restate `price`, quoted per `fromUom`, as a price per `toUom`.
 *
 * Full precision, no rounding — see the header. Returns null when either side is
 * underivable, which callers surface as a refusal rather than a wrong number.
 */
export function convertUnitPrice({
  price,
  fromUom,
  toUom,
  pack,
}: {
  price: unknown;
  fromUom: unknown;
  toUom: unknown;
  pack: unknown;
}): number | null {
  const value = positive(price);
  if (value == null) return null;
  const both = piecesOfBoth(fromUom, toUom, normalizePackStructure(pack));
  return both == null ? null : value * (both.to / both.from);
}

/**
 * Restate `quantity`, counted in `fromUom`, as a quantity of `toUom`.
 *
 * The RATIO IS THE RECIPROCAL of `convertUnitPrice`'s, and that is the entire
 * point of the function: `price * quantity` is invariant across a conversion by
 * construction, because one is multiplied by `to/from` and the other by
 * `from/to`. Restating the unit a line is quoted in must not restate the money
 * on it, and must not restate how much physical stuff the vessel asked for.
 *
 * Without this, the screen converted the PRICE and carried the QUANTITY over
 * verbatim, so an RFQ asking for 20 PCS of a 10-per-case item — US$10.00/PCS,
 * US$200.00 — became "20 CASE at US$100.00" and quoted **US$2,000.00**. The
 * error runs both ways: the same line switched to LBS became 20 lb of a 20 kg
 * request and quoted US$90.72. Only 2 CASE at US$100.00 is both the price the
 * RFQ asked to be quoted and the amount it asked for.
 *
 * Full precision and no rounding, for the same reason as the price: the caller
 * quantises once, at the edge. Rounding a quantity mid-chain is the same ratchet
 * ceiling a price mid-chain was (see the header) — 25 PCS rounded up to 3 CASE
 * comes back as 30 PCS.
 *
 * Returns null when either side is underivable, which callers surface as a
 * refusal rather than a wrong number.
 */
export function convertQuantity({
  quantity,
  fromUom,
  toUom,
  pack,
}: {
  quantity: unknown;
  fromUom: unknown;
  toUom: unknown;
  pack: unknown;
}): number | null {
  const value = positive(quantity);
  if (value == null) return null;
  const both = piecesOfBoth(fromUom, toUom, normalizePackStructure(pack));
  return both == null ? null : value * (both.from / both.to);
}

/**
 * The one quantiser for a converted quantity, applied by the caller at the edge.
 *
 * This does not contradict "no rounding in here" above — the CONVERSION still
 * returns full precision, and a chain of them is computed from the anchor, so
 * nothing rounds mid-chain and nothing ratchets. This is the single named place
 * that decides how a converted quantity is finally written, so the browser and
 * the server cannot disagree about it the way two `num()` copies once did.
 *
 * Six decimals: enough to erase floating-point dust (a kg -> lb -> kg round trip
 * landing on 19.999999999999996 is stored as 20), and finer than any pack ratio
 * this system holds — a milligram, at kilogram scale.
 *
 * It deliberately does NOT snap to whole units. 25 PCS of a 10-per-case item is
 * 2.5 CASE, and rounding that up to 3 both overcharges by 20% and changes what
 * the vessel is sent. How to handle a part-case is the quoter's call, made on a
 * number that is honest about being fractional.
 */
export function roundQuantity(quantity: unknown): number | null {
  const value = positive(quantity);
  if (value == null) return null;
  return Math.round(value * 1e6) / 1e6;
}

/**
 * ── A PO line in the supplier's pack (D82) ─────────────────────────────────────
 * The supplier sells cases of 25; the ship wants 110 PCS. 110 / 25 = 4.4 CS: not
 * a whole number of cases, so staff choose up (5), down (4) or type a count.
 * The PO page labels its buttons with these, the round endpoint writes them.
 *
 * `packs` is on `roundQuantity`'s grid, so float dust is gone BEFORE anything
 * is judged: 0.30000000000000004 / 0.1 is exactly 3 packs, not "3, round up to
 * 4". On that grid `Number.isInteger` asks the same question
 * `unitConversionGraph.quantitiesAgree(packs, Math.round(packs))` would.
 *
 * Null when either figure is missing or not a positive number — never a 0 that
 * would offer "0 CS". Like `positive` above this is a guard, not a parser: a
 * string is refused, not read.
 */
export type PackRoundingChoices = { packs: number; up: number; down: number; exact: boolean };

export function packRoundingChoices(requestedQuantity: unknown, piecesPerPack: unknown): PackRoundingChoices | null {
  const requested = positive(requestedQuantity);
  const perPack = positive(piecesPerPack);
  if (requested == null || perPack == null) return null;
  const packs = roundQuantity(requested / perPack);
  if (packs == null || packs <= 0) return null;
  const exact = Number.isInteger(packs);
  return { packs, up: Math.ceil(packs), down: Math.floor(packs), exact };
}

/**
 * A count of packs someone typed (the PO page's "Other...", the round
 * endpoint's manual decision): a positive WHOLE number, or null. Cases are
 * whole — 4.5 CS is refused, not rounded. Judged on `roundQuantity`'s grid, so
 * 3.0000000000000004 is 3.
 */
export function wholePackCount(value: unknown): number | null {
  const q = roundQuantity(value);
  return q != null && q > 0 && Number.isInteger(q) ? q : null;
}

/**
 * What is wrong with the quantity and prices typed on a PO line, or null. THE sign rule of a
 * hand edit: the PO page shows it before saving and the PUT
 * (`vendorPurchaseOrder.keepServerOwnedLineFields`) refuses on it, both through this.
 *
 * Only what is being CHANGED is judged: a value left exactly as stored is never refused, so a PO
 * that already holds an old 0 or negative (a line removed from its order, D81, is stored at 0; an
 * old hand edit stored a negative) still saves every other edit, since the page sends every line
 * on every save. A changed quantity must be above 0 (a blank counts as 0 against the stored one:
 * it is how the PUT stores a missing quantity); a changed quantity or price is never negative. A
 * price of 0 or no price is allowed (unpriced).
 *
 * Takes already-parsed numbers: null is missing, never 0.
 */
export type PoLineAmountProblem = 'quantity_negative' | 'quantity_not_positive' | 'price_negative';
type PoLineAmounts = { quantity?: number | null; unitPrice?: number | null; totalPrice?: number | null };
export function poLineAmountProblem(
  typed: { quantity: number | null; unitPrice: number | null; totalPrice: number | null },
  stored: PoLineAmounts | null | undefined
): PoLineAmountProblem | null {
  const { quantity, unitPrice, totalPrice } = typed;
  const negative = (n: number | null) => n != null && n < 0;
  const asStored = (typedValue: number | null, storedValue: number | null | undefined) => !!stored && typedValue === (storedValue ?? null);
  const quantityAsStored = !!stored && (quantity == null ? !stored.quantity : quantity === stored.quantity);
  if (negative(quantity) && !quantityAsStored) return 'quantity_negative';
  if ((negative(unitPrice) && !asStored(unitPrice, stored?.unitPrice)) || (negative(totalPrice) && !asStored(totalPrice, stored?.totalPrice))) return 'price_negative';
  if (!(quantity != null && quantity > 0) && !quantityAsStored) return 'quantity_not_positive';
  return null;
}

/** The D82 block on a PO line — structurally what `packPiecesNote` reads. */
export type PoLinePackConversion = {
  status: string;
  packUnit: string | null;
  piecesPerPack: number | null;
  requestedUom: string | null;
  packText?: string | null;
};

/** A PO line as `orderedPieces` and `packPiecesNote` read it. */
export type PoLinePieces = {
  quantity: unknown;
  uom: unknown;
  packConversion?: PoLinePackConversion | null;
};

/**
 * Was this PO line written in its supplier's pack — 'applied' (divided evenly at
 * generation) or 'decided' (staff chose the packs)? THE status rule: the PO
 * page's unit lock and whole-pack flag, the hand-edit gate
 * (`vendorPurchaseOrder.service.assertPackLineEditable`), the re-pricer and
 * `isInPackUnit` all ask this, never their own spelling. 'pending' is not: that
 * line is still in the unit asked. Missing is false.
 */
export function isPackApplied(pc: { status?: string | null } | null | undefined): boolean {
  return !!pc && (pc.status === 'applied' || pc.status === 'decided');
}

/**
 * Is this PO line written in its supplier's pack? Only while the conversion is
 * `isPackApplied` AND the line is still in the pack unit (`isSameUnit`, so
 * CASE/CS/cases are one): a line hand-edited back to pieces has no pack to
 * explain or multiply. The one condition `packPiecesNote` and `orderedPieces`
 * share.
 */
function isInPackUnit(uom: unknown, pc: PoLinePackConversion): boolean {
  return isPackApplied(pc) &&
    !!pc.packUnit && !!pc.requestedUom && isSameUnit(uom, pc.packUnit);
}

/**
 * How many of the unit it was ASKED in (`requestedUom`, e.g. PCS) a PO line
 * orders — the one answer to "4 CS is how many pieces". Readers that compare a
 * PO line with the order line it buys (the client invoice's actual cost) count
 * in this, never in the PO line's own `quantity`, which may be packs.
 *
 *   - in its pack unit (`isInPackUnit`): quantity x pieces per pack, on
 *     `roundQuantity`'s grid (4 CS x 25 = 100);
 *   - never converted (no block — a PO from before D82 — or 'none'): the
 *     quantity itself, in the line's own unit, as every reader always read it;
 *   - converted or pending, but in the unit asked in: the quantity itself;
 *   - anything else is null — a pack with no count, a pending line typed into
 *     cases before anyone decided, a converted line hand-edited into a third
 *     unit. Unknown, never the pack count read as pieces (a 25x cost).
 * Null too when the quantity is not a positive number (a guard, not a parser).
 */
export function orderedPieces({ quantity, uom, packConversion }: PoLinePieces): number | null {
  const qty = positive(quantity);
  if (qty == null) return null;
  const pc = packConversion;
  if (pc && isInPackUnit(uom, pc)) {
    const perPack = positive(pc.piecesPerPack);
    return perPack == null ? null : roundQuantity(qty * perPack);
  }
  if (!pc || !pc.status || pc.status === 'none') return qty;
  return pc.requestedUom && isSameUnit(uom, pc.requestedUom) ? qty : null;
}

/**
 * `orderedPieces`' twin for a price: one pack holds `piecesPerPack` of the asked unit, of any kind (25 PCS, 9.08 KG,
 * 1.5 PACK), so the pack price over it is the price per asked unit, and back (D170.1). Null for any other pair of units.
 */
export function packUnitPrice({ price, fromUom, toUom, packConversion: pc }: {
  price?: unknown; fromUom?: unknown; toUom?: unknown; packConversion?: PoLinePackConversion | null;
} = {}): number | null {
  const value = positive(price);
  const perPack = positive(pc?.piecesPerPack);
  if (value == null || perPack == null || !pc?.packUnit || !pc.requestedUom) return null;
  if (isSameUnit(fromUom, pc.packUnit) && isSameUnit(toUom, pc.requestedUom)) return value * (1 / perPack);
  if (isSameUnit(fromUom, pc.requestedUom) && isSameUnit(toUom, pc.packUnit)) return value * perPack;
  return null;
}

/**
 * The note a line written in the supplier's pack carries: "(25 PCS/CS = 100
 * PCS)", the pieces being `orderedPieces` — what the supplier will send, not
 * what was asked. Only while `isInPackUnit`. Null when a figure is missing.
 */
export function packPiecesNote({ quantity, uom, packConversion }: PoLinePieces): string | null {
  const pc = packConversion;
  if (!pc || !isInPackUnit(uom, pc)) return null;
  const pieces = orderedPieces({ quantity, uom, packConversion });
  if (pieces == null) return null;
  return `(${pc.piecesPerPack} ${pc.requestedUom}/${pc.packUnit} = ${pieces} ${pc.requestedUom})`;
}

/**
 * D169.6 - what one pack holds, in the words stored with it (`packText`, "CASE of 40 x 227 G"), so the supplier does
 * not work it out from "(9.08 KG/CASE ...)". Only while `isInPackUnit`. Null when nothing was stated: never made up.
 */
export function packContentsNote({ uom, packConversion }: Pick<PoLinePieces, 'uom' | 'packConversion'>): string | null {
  const pc = packConversion;
  if (!pc || !isInPackUnit(uom, pc)) return null;
  const text = typeof pc.packText === 'string' ? pc.packText.trim() : '';
  return text || null;
}

/**
 * Which of `candidates` this line can actually be restated in, in the order
 * given and without duplicates. The dropdown offers only these, so a unit that
 * would fail is never presented in the first place.
 */
export function convertibleUnits(fromUom: unknown, candidates: unknown, pack: unknown): string[] {
  const p = normalizePackStructure(pack);
  if (piecesPerUnit(fromUom, p) == null) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of Array.isArray(candidates) ? candidates : []) {
    const u = canonicalUnit(c);
    if (!u || seen.has(u)) continue;
    if (piecesOfBoth(fromUom, u, p) == null) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

/** A Unit Size read as a count times a size — see `readCountTimesSize`. */
export type CountTimesSize = { count: number; each: number; unit: string };

/**
 * A Unit Size that states a COUNT TIMES A SIZE, read into its three parts —
 * `"20ct*1.5oz"` -> `{ count: 20, each: 1.5, unit: 'OZ' }` — or null.
 *
 * ── Why this exists (prod USC-BUG-0030 / 0031 / 0032) ────────────────────────
 * Sea Food Enterprises 127632-12. The reviewer entered "1 case = 20 packs" and,
 * in Unit Size, "20ct*1.5oz": twenty 1.5 oz pieces, 30 oz to a pack. Two readers
 * of that one cell disagreed. `unitVocabulary.splitSizeText` refused it, so the
 * catalogue row got no "1 PCS = ... OZ" conversion, while `itemGroup.parseSize`
 * skipped the "20ct" and read 1.5 oz, so the row's per-kg and per-lb prices
 * (938.66 and 426.67 on screen) were twenty times too high.
 *
 * The same shape already means something else on prod: "12x28oz" is twelve cans
 * of 28 oz, where 12 is the CASE count. The text cannot say which is meant, so
 * this only READS the shape. The reviewer says which reading it is (owner's
 * decision 2026-09-15: ask on review), and `invoiceLineMath.lineUnitSize`
 * applies the answer — BUSINESS_RULES#unit-size-count-times-size.
 *
 * ── The shape, and what is refused ───────────────────────────────────────────
 * The WHOLE cell is `N [CT|PC|PCS|EA] x|X|* size unit`. Anchored at both ends,
 * so prose is never read: "PASTA 20 X 500G" stays `parseSize`'s description
 * reading (pinned by `weightTierDescriptionRestraint101.test.js`). The count is
 * a whole number above zero and the size a number above zero, so "0x5g" and
 * "1.5x2kg" are refused. The unit must be a mass or volume unit this file
 * converts (`KG_PER_UNIT`, `LITRE_PER_UNIT`): a length ("10x15cm"), a pint (no
 * convention yet, TECH_DEBT #144), a size grade ("16-20") and a bare pack config
 * ("6x5") all come back null.
 *
 * In this file rather than `unitVocabulary` because the unit test IS these two
 * tables, and `unitVocabulary` cannot import them: this file imports it.
 *
 * Numbers come back as numbers, because every caller multiplies them. The
 * pattern has already proved each is a plain decimal, so `Number` here is not a
 * second `num()` (invariant 8) — the same call `unitVocabulary.packCountFromUomText`
 * makes on its own matched digits.
 */
export const COUNT_TIMES_SIZE_RE = /^(\d+)\s*(?:CT|PCS?|EA)?\s*[xX*]\s*(\d*\.?\d+)\s*([a-zA-Z]+)$/i;

export function readCountTimesSize(text: unknown): CountTimesSize | null {
  const m = String(text == null ? '' : text).trim().match(COUNT_TIMES_SIZE_RE);
  if (!m) return null;
  const unit = canonicalUnit(m[3]);
  if (!unit || (KG_PER_UNIT[unit] == null && LITRE_PER_UNIT[unit] == null)) return null;
  const count = Number(m[1]);
  const each = Number(m[2]);
  if (!(count > 0) || !(each > 0)) return null;
  return { count, each, unit };
}

/**
 * A size BAND - "3-5 lb", "2-3 kg", "10-12 oz", "10–12 oz": a hyphen or a dash between two numbers
 * that RISE and stay NARROW, low < high <= 3 x low (16-20, 10-12, 3-5, 1-3, 0.5-1) - is no count and
 * not one size. Shrimp, lobster, fish and meat are sold in bands, and the rest of the code already
 * knows it: `cleanItemName` strips them as grades, `num.readQuantity` refuses them as quantities, a
 * unit cell reading "6-8oz" is junk. Read as a pack, "Tilapia fillet 3-5 lb" held 3 pieces of 5 lb
 * (QUO-00243 follow-up).
 *
 * Only that shape. "6-1L" falls and "24-330ml" is far wider than any band: they are packs written
 * with a hyphen (6 x 1 L, 24 x 330 ml) and read as they always did. A date ("2026-10-08",
 * "08-10-2026") is a chain of three numbers and a part number is long: neither is a band. A band is
 * followed by a word (a unit or a grade name), or by "x ..." when it is a band of COUNTS
 * ("6-8 x 300g"), and that goes with it. Numbers of up to four digits, not glued to another number.
 *
 * D105.10: such a pair is AMBIGUOUS ("6-12oz" may be 6 x 12 oz or a size range), so no pack is read
 * from it and the quote line is flagged for review (`hasSizeRange` is the one asker; the pricing answer
 * carries it as `rfqWordsAmbiguous`, and `resolveQuotedUnit` stores the reason on the line as
 * `unitMismatchReason: 'pack_ambiguous'`). Clear packs are not bands.
 *
 * Global, for `replace` and `matchAll`: a regex used with `.test` would carry its position.
 */
// The dashes a band is written with: hyphen-minus, the Unicode hyphens and dashes (U+2010 to
// U+2015, the en and em dash among them) and the minus sign.
const BAND_DASH = '[-\u2010-\u2015\u2212]';
const SIZE_RANGE_RE = new RegExp(
  String.raw`(?<!\d\.?|\d\s*${BAND_DASH}\s*)(\d{1,4}(?:\.\d+)?)\s*${BAND_DASH}\s*(\d{1,4}(?:\.\d+)?)` +
    String.raw`(?!\d|\s*${BAND_DASH}\s*\d)(?=\s*[a-zA-Z*])((?:\s*[xX*]\s*\d+(?:\.\d+)?)*)`,
  'g'
);
const isSizeBand = (low: string, high: string): boolean => {
  const lo = Number(low);
  const hi = Number(high);
  return lo > 0 && lo < hi && hi <= 3 * lo;
};

// D157.12: a slash pair before a COUNT word is a size grade ("SHRIMP 21/25 CT", 21 to 25 a pound),
// flagged like a dash band. It must be a band too: "12/250 CT" is no grade, "12/28oz" stays a pack.
const SLASH_GRADE_RE = /(?<![\d./])(\d{1,4})\s*\/\s*(\d{1,4})(?=\s*([a-zA-Z]+))/g;
const isCountWord = (word: string): boolean => canonicalUnit(word) === 'PCS';
const isSlashGrade = (low: string, high: string, word: string): boolean => isCountWord(word) && isSizeBand(low, high);

/** Does the text state a size band or grade? The one reading, for the callers that must ask. */
export function hasSizeRange(text: unknown): boolean {
  const s = String(text == null ? '' : text);
  for (const m of Array.from(s.matchAll(SIZE_RANGE_RE))) {
    if (isSizeBand(m[1], m[2])) return true;
  }
  for (const m of Array.from(s.matchAll(SLASH_GRADE_RE))) {
    if (isSlashGrade(m[1], m[2], m[3])) return true;
  }
  return false;
}

/**
 * A pack read out of an item's words: `packCount` pieces of `unitSize` `unitUom`. The size is null
 * when the words give a count and no measure; `pieceUom` names a counted piece ("48 cans" -> TIN).
 * `countOnly`: a count and nothing beside it ("12pk", "case of 24"), the weakest pack evidence.
 */
export type PackPattern = {
  packCount: number;
  unitSize: number | null;
  unitUom: string | null;
  pieceUom?: string;
  countOnly?: true;
};
/** A size read out of an item's words. */
export type UnitSizeReading = { unitSize: number; unitUom: string };

// D157.12: a pack stated by its count alone. "12pk", "6-pack", "48 cans", "24 CT"; "case of 24";
// "24/case", "12/cs". Never joined to a letter, hyphen or number before it ("WD-40 CAN", "3723055-00
// PCS" are codes), nor times a size after it ("20ct*1.5oz" is count x size, read before this).
const COUNT_THEN_WORD_RE = /(?<![\d.a-zA-Z-]|\d\s*[-/]\s*)(\d{1,4})\s*-?\s*([a-zA-Z]+)\b(?!\s*[xX*/]\s*\.?\d)/g;
const CONTAINER_OF_COUNT_RE = /\b([a-zA-Z]+)\s+of\s+(\d{1,4})\b/gi;
const COUNT_PER_CONTAINER_RE = /(?<![\d.a-zA-Z-]|\d\s*[-/]\s*)(\d{1,4})\s*\/\s*([a-zA-Z]+)/g;

/** `count` pieces of a measure, or of a size the words do not give (a container is no size). */
function sizedPack(count: number, size: number | null = null, unit: string | null = null): PackPattern {
  return isDimensionUnit(unit)
    ? { packCount: count, unitSize: size, unitUom: unit }
    : { packCount: count, unitSize: null, unitUom: null };
}

/** The count a count-only phrase states, with the piece it counts when the word names one. */
function countOnlyPack(text: string): PackPattern | null {
  for (const m of Array.from(text.matchAll(COUNT_THEN_WORD_RE))) {
    const word = canonicalUnit(m[2]);
    const n = Number(m[1]);
    if (n > 1 && (word === 'PACK' || isSingleItemUnit(word))) {
      return { ...sizedPack(n), ...(word && isSingleItemUnit(word) ? { pieceUom: word } : {}) };
    }
  }
  const containers: [RegExp, number, number][] = [[CONTAINER_OF_COUNT_RE, 1, 2], [COUNT_PER_CONTAINER_RE, 2, 1]];
  for (const [re, wordAt, countAt] of containers) {
    for (const m of Array.from(text.matchAll(re))) {
      if (isGroupingUnit(m[wordAt]) && Number(m[countAt]) > 1) return sizedPack(Number(m[countAt]));
    }
  }
  return null;
}

/**
 * The one reader of a pack in an item's WORDS ("24X300G" -> 24 x 300 G), for pricing, the bill
 * processor and the review page alike: the same words never give two answers (D157.12).
 * A count with no measure ("12pk", "6X5 CASE") reads its size as null; `pieceUom` names a counted
 * piece ("48 cans" -> TIN). Size first ("500ml x24") reads as count first; "1/2 GAL" is half a gallon.
 * `countOnly`: a count and nothing beside it ("12pk", "case of 24"), the weakest pack evidence.
 */
export function parsePackPattern(desc: unknown): PackPattern | null {
  const trimmed = String(desc || '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return null;
  // The bands and grades go first: what is left is read as it would be without them, so "FILLET
  // 3-5 OZ 20x500g" is 20 x 500 g. A pair that is no band stays, and a hyphen in it is a pack separator.
  const text = trimmed
    .replace(SIZE_RANGE_RE, (m: string, low: string, high: string) => (isSizeBand(low, high) ? ' ' : m))
    .replace(SLASH_GRADE_RE, (m: string, low: string, high: string, word: string) => (isSlashGrade(low, high, word) ? ' ' : m));

  const multi = text.match(
    /(\d+(?:\.\d+)?)\s*[xX*]\s*(\d+(?:\.\d+)?)\s*[xX*]\s*(\d+(?:\.\d+)?)[\s]*([a-zA-Z]+)/
  );
  if (multi) {
    const a = Number(multi[1]);
    const b = Number(multi[2]);
    const size = Number(multi[3]);
    const unit = canonicalUnit(multi[4]);
    if (Number.isFinite(a) && Number.isFinite(b) && Number.isFinite(size) && unit) {
      return sizedPack(a * b, size, unit);
    }
  }

  const single = text.match(/(\d+(?:\.\d+)?)\s*([xX*/-])\s*(\d+(?:\.\d+)?)[\s]*([a-zA-Z]+)/);
  if (single) {
    const count = Number(single[1]);
    const size = Number(single[3]);
    const unit = canonicalUnit(single[4]);
    if (Number.isFinite(count) && Number.isFinite(size) && unit) {
      // "1/N unit" is one piece, sized by the measure reader, which owns the fraction ("1/2 GAL").
      const one = single[2] === '/' && single[1] === '1' ? measureFromText(single[0]) : null;
      if (one) return { packCount: 1, unitSize: Number(one.amount), unitUom: one.unit };
      return sizedPack(count, size, unit);
    }
  }

  // A count word between the count and the x ("20ct*1.5oz", "24 CT x 330ML") is still count x size.
  const counted = text.match(/(?<![\d.])(\d+)\s*(?:CT|PCS?|EA)\s*[xX*]\s*(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/i);
  const countedUnit = counted ? canonicalUnit(counted[3]) : null;
  if (counted && countedUnit && isDimensionUnit(countedUnit)) {
    return { packCount: Number(counted[1]), unitSize: Number(counted[2]), unitUom: countedUnit, pieceUom: 'PCS' };
  }

  // Size first: "500ml x24" is 24 x 500 ml, a measure and then a whole count.
  for (const m of Array.from(text.matchAll(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+?)\s*[xX*]\s*(\d+)(?![\d.])/g))) {
    const unit = canonicalUnit(m[2]);
    if (unit && isDimensionUnit(unit)) return { packCount: Number(m[3]), unitSize: Number(m[1]), unitUom: unit };
  }

  const alone = countOnlyPack(text);
  return alone && { ...alone, countOnly: true };
}

/**
 * The pack the words of a line sold per `uom` state, for pricing, the bill processor and the review
 * page alike (D157.12). `countWords: false` drops a count-only reading, a suggestion never a stated
 * cell. USC-BUG-0030: "20ct*1.5oz" is 20 pieces or one 1.5 oz piece; on a PCS line the Unit Size
 * decides, so there those words count nothing.
 */
export function packFromWords(desc: unknown, uom: unknown, { countWords = true }: { countWords?: boolean } = {}): PackPattern | null {
  const read = parsePackPattern(desc);
  if (!read || (read.countOnly && !countWords)) return null;
  return read.pieceUom === 'PCS' && read.unitSize !== null && canonicalUnit(uom) === 'PCS' ? null : read;
}

/**
 * The first MEASURE in the words ("RICE 10KG" -> 10 KG, "24 CANS 330ML" -> 330 ML), through
 * `measureFromText`: a count word is never a size (D157.12). A band is not stripped here.
 */
export function parseUnitSize(desc: unknown): UnitSizeReading | null {
  const measure = measureFromText(desc);
  const size = measure ? Number(measure.amount) : NaN;
  return measure && Number.isFinite(size) ? { unitSize: size, unitUom: measure.unit } : null;
}

/** The parts of a quotation line the packaging remark reads and writes. */
export type PackagingRemarkLine = {
  uom?: unknown;
  remarks?: unknown;
  packagingRemark?: unknown;
  pricingDetails?: unknown;
};

/**
 * THE CLIENT-VISIBLE PACKAGING SENTENCE — "Case of 25 × 1 KG", "Box of 24 × 200 G",
 * "Case of 12 PCS". Owner decision 2026-09-26.
 *
 * Clients misread a "per case" price when the case's contents are not stated —
 * a competitor quotes a case with fewer pieces and USC's looks dear. So a line
 * PRICED PER A PACK states what the pack holds, in the remark the client reads.
 *
 * Only for a GROUPING unit (`unitVocabulary.isGroupingUnit`: CASE, BOX, CARTON,
 * PACK, BUNDLE, TRAY) — a per-KG, per-L, per-piece or per-bottle line gets
 * nothing, and BAG/SACK stay out for the reason they are unclassified there.
 * Only when the line's own pack (`displayEquivalents.packStructure`) names THAT
 * unit as its outer and states its count: a CASE line whose pack is a BOX says
 * nothing about the case. The piece size is the line's `displayEquivalents
 * .unitSize` read strictly (`splitSizeText`, one number and one measure unit);
 * anything else ("12x28oz", a grade) is left out rather than half-read, and the
 * sentence falls back to the count — only a whole count above one, else
 * nothing. A pack of ONE with a size states the size alone.
 */
export function packagingRemark(line: PackagingRemarkLine | null | undefined): string | null {
  const unit = canonicalUnit(line && line.uom);
  if (!line || !unit || !isGroupingUnit(unit)) return null;
  const details = (line.pricingDetails || {}) as { displayEquivalents?: Record<string, unknown> | null };
  const eq = details.displayEquivalents || {};
  const pack = normalizePackStructure(eq.packStructure);
  if (pack.outerUom !== unit) return null;
  const word = unit.charAt(0) + unit.slice(1).toLowerCase();
  const size = splitSizeText(eq.unitSize);
  const sizeUnit = size ? canonicalUnit(size.unit) : null;
  if (!size || !sizeUnit || !isDimensionUnit(sizeUnit)) {
    // A count alone says something only as a whole number above one: "Case of
    // 1 PCS" tells the client nothing, and "Case of 2.5 PCS" is not a pack.
    const count = pack.piecesPerOuter;
    return count != null && Number.isInteger(count) && count > 1 ? `${word} of ${count} PCS` : null;
  }
  const each = `${size.amount} ${sizeUnit}`;
  return pack.piecesPerOuter === 1 ? `${word} of ${each}` : `${word} of ${pack.piecesPerOuter} × ${each}`;
}

/**
 * A line's `remarks` with the recorded packaging sentence (`packagingRemark`)
 * taken off the front — the staff text alone, trimmed. Only that EXACT prefix,
 * ending the remark or followed by "."; anything else (staff reworded or moved
 * it, or nothing is recorded) comes back whole. The one strip rule: the apply
 * step below uses it, and so does the vendor PO, which carries only
 * human-typed text (D14; owner decision 2026-09-26).
 */
export function remarksWithoutPackagingRemark(line: PackagingRemarkLine | null | undefined): string {
  const l: PackagingRemarkLine = line || {};
  const text = String(l.remarks == null ? '' : l.remarks).trim();
  const prev = typeof l.packagingRemark === 'string' && l.packagingRemark ? l.packagingRemark : null;
  if (!prev || !text.startsWith(prev)) return text;
  const rest = text.slice(prev.length);
  return rest === '' || rest.startsWith('.') ? rest.replace(/^\.\s*/, '') : text;
}

/**
 * Put `packagingRemark(line)` at the front of the line's client `remarks`, as a
 * PATCH — `{remarks, packagingRemark}`, or `{}` when nothing changes.
 *
 * The one apply step every pricing path calls (generation, the amendment's added
 * lines, the supplier pick, and on the grid the catalogue pick, Refresh price
 * and the Unit dropdown), so the rules hold on all of them:
 *   - PREPEND ALWAYS (owner): "<sentence>. <staff text>", or the sentence alone.
 *   - The exact text written is kept on the line as `packagingRemark`, and only
 *     that exact prefix (ending the remark or followed by ".") is ever taken
 *     back out — staff prose is never pattern-matched. A changed pack REPLACES
 *     it; a line no longer pack-priced LOSES it; applying twice is a no-op.
 *   - Staff removed or reworded the sentence (it is no longer the prefix): the
 *     SAME sentence is not written back — they meant it. A DIFFERENT one (the
 *     pack really changed) is still prepended, because the client must be told
 *     the new pack; their text stays as they left it.
 *   - Staff already typed the exact sentence first: adopted, never doubled.
 */
export function applyPackagingRemark(
  line: PackagingRemarkLine | null | undefined,
): { remarks: string; packagingRemark: string | null } | Record<string, never> {
  const l: PackagingRemarkLine = line || {};
  const text = String(l.remarks == null ? '' : l.remarks).trim();
  const prev = typeof l.packagingRemark === 'string' && l.packagingRemark ? l.packagingRemark : null;
  const next = packagingRemark(l);
  const staff = remarksWithoutPackagingRemark(l);
  // Taking a non-empty prefix out always changes the text, so this is "it was there".
  const prefixed = staff !== text;

  let out: { remarks: string; packagingRemark: string | null };
  if (prev && !prefixed && next === prev) out = { remarks: text, packagingRemark: prev };
  else if (!next) out = { remarks: staff, packagingRemark: null };
  else if (staff === next || staff.startsWith(`${next}.`)) out = { remarks: staff, packagingRemark: next };
  else out = { remarks: staff ? `${next}. ${staff}` : next, packagingRemark: next };

  return out.remarks === text && out.packagingRemark === prev ? {} : out;
}
